import React from 'react';
import { BaseEdge, EdgeLabelRenderer, EdgeProps, getBezierPath } from 'reactflow';
import { Box, Flex, Icon, Text } from 'pounceV2';
import { toPlural } from 'Helpers/utils';
import { useTheme } from '@mui/material';
import { getFlowChartColors } from './constantsV3';
import { TransitionData } from './utils';

export interface TransitionEdgeData {
  transitionData?: TransitionData; // data from the YAML
  error?: string;
}

const TransitionEdgeV3: React.FC<EdgeProps<TransitionEdgeData>> = props => {
  const theme = useTheme();
  const FLOW_CHART_COLORS = getFlowChartColors(theme.palette.tokens);
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    data,
    markerEnd,
  } = props;
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: FLOW_CHART_COLORS.TRANSITION.BORDER,
        }}
      />
      {data?.transitionData?.WithinTimeFrameMinutes ||
      data?.transitionData?.Match ||
      data?.error ? (
        <EdgeLabelRenderer>
          <Flex
            zIndex={1000}
            position="absolute"
            transform={`translate(-50%, -50%) translate(${labelX}px,${labelY}px)`}
            pointerEvents="all"
            align="center"
            gap={2}
            background={FLOW_CHART_COLORS.TRANSITION.BACKGROUND}
            border="1px solid"
            borderRadius="circle"
            borderColor={FLOW_CHART_COLORS.TRANSITION.BORDER}
            p={2}
            sx={{
              backdropFilter: 'blur(12px)',
            }}
            _hover={{
              backgroundColor: FLOW_CHART_COLORS.TRANSITION.BACKGROUND_HOVER,
              borderColor: FLOW_CHART_COLORS.TRANSITION.BORDER_HOVER,
              cursor: 'default',
            }}
          >
            {data?.error ? (
              <Flex gap={2} align="center">
                <Text>
                  <Icon size="x-small" type="warning" m="auto" display="block" />
                </Text>
                <Text textAlign="center" variant="body/sm">
                  {data.error}
                </Text>
              </Flex>
            ) : (
              <>
                {data?.transitionData?.Match ? (
                  <Box
                    backgroundColor={FLOW_CHART_COLORS.TRANSITION.BORDER}
                    borderRadius="circle"
                    p={1}
                  >
                    <Icon size="small" type="copy-link" m="auto" display="block" />
                  </Box>
                ) : null}
                {data?.transitionData?.WithinTimeFrameMinutes ? (
                  <Flex gap={2} align="center">
                    <Text>
                      <Icon size="x-small" type="clock" m="auto" display="block" />
                    </Text>
                    <Text textAlign="center" variant="body/sm">
                      within {data.transitionData.WithinTimeFrameMinutes}{' '}
                      {toPlural('minute', data.transitionData.WithinTimeFrameMinutes)}
                    </Text>
                  </Flex>
                ) : null}
              </>
            )}
          </Flex>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
};

export default TransitionEdgeV3;
